import { useMemo, useRef, useState } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { IconListSearch, IconLoader2, IconRefresh } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { useGetBookingQuery } from "@/app/booking-calendar/bookingCalendarApi"
import { AppointmentDialog } from "@/app/booking-calendar/components/modals"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import { useAppDispatch, useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"
import { SearchCriteriaForm } from "./components/SearchCriteriaForm"
import { SearchResults, type SearchResultsState } from "./components/SearchResults"
import {
  DEFAULT_PAGE_SIZE,
  REVEAL_SSN_PERMISSION,
  SSN_SEARCH_TAKE,
  buildSearchRequest,
  buildSsnSearchRequest,
  defaultSearchForm,
  isSsnSearch,
  validateSearchForm,
  type PageSize,
  type SearchForm,
} from "./searchCriteria"
import {
  bookingSearchApi,
  searchErrorMessage,
  useSearchBookingRequestsQuery,
  type BookingSearchResult,
} from "./searchApi"
import { runSsnSearch, type SsnSearchOutcome } from "./ssnSearch"

/**
 * Appointments → Search (#770): every site's appointments, past and future, found by what staff know
 * about them — part of a name or address, a phone number, an email address, a booking reference or
 * a status — inside a Pacific date range, or everything in the range when nothing else is given.
 *
 * The criteria are sent in a POST body and held only in this page's state and RTK Query's in-memory
 * cache, which drops them as soon as the page closes; nothing is written to storage.
 *
 * A Social Security number (#771), offered only with appointments:reveal-ssn, is held less long still:
 * it is cleared from the form as the search is sent, goes through a mutation rather than the query so
 * it is never a cache key, and the mutation is reset in the store as soon as it answers (see
 * ssnSearch.ts). The mutation is dispatched directly, never through its generated hook: the hook keeps
 * the request it last sent — the number included — in this component's own state until the next
 * call or unmount. Only the outcome (results or a fixed message) is kept here; the last criteria used
 * for paging and refresh never include the number. Its results are one page and are not refreshed,
 * since either would mean sending the number again.
 *
 * Choosing a result opens the same appointment dialog the calendar uses. A request that never reached
 * the scheduling system has nothing to open, so its row is not a control.
 */
export default function AppointmentSearchPage() {
  const [form, setForm] = useState<SearchForm>(() => defaultSearchForm())
  // The criteria last searched for: paging moves through them, while edits to the form wait for Search.
  const [searched, setSearched] = useState<SearchForm | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE)
  const [selected, setSelected] = useState<BookingSearchResult | null>(null)
  // The last search by Social Security number: its results or why it failed, never the number.
  const [ssnOutcome, setSsnOutcome] = useState<SsnSearchOutcome | null>(null)
  // Which search by number is the current one, so an answer that arrives after Clear or a newer
  // search is dropped rather than shown.
  const ssnSearchId = useRef(0)

  const { permissions } = useAppSelector(state => state.permissions)
  const ssnAllowed = hasPermission(permissions, REVEAL_SSN_PERMISSION)
  const dispatch = useAppDispatch()

  const validation = useMemo(() => validateSearchForm(form, { ssnAllowed }), [form, ssnAllowed])
  const request = useMemo(() => (searched ? buildSearchRequest(searched, page, pageSize) : null), [searched, page, pageSize])

  const query = useSearchBookingRequestsQuery(request ?? skipToken)

  // The dialog takes Cal's booking, which a result does not carry — it is fetched on selection.
  const selectedBooking = useGetBookingQuery(selected?.calBookingUid ?? "", { skip: !selected?.calBookingUid })

  // A new search shows its own loading state rather than the previous search's rows; a refresh of the
  // same one (after a change in the dialog) keeps the rows on screen while it is fetched.
  const state: SearchResultsState = ssnOutcome
    ? ssnOutcome.state
    : !request
      ? "idle"
      : query.isError
        ? "error"
        : query.currentData
          ? "ready"
          : "loading"

  const search = () => {
    if (!validation.valid) return
    setSelected(null)

    if (isSsnSearch(form, { ssnAllowed })) {
      const body = buildSsnSearchRequest(form)
      // Cleared as it is sent: the field never holds the number past this point.
      setForm(current => ({ ...current, ssn: "" }))
      setSearched(null)
      setPage(0)
      setSsnOutcome({ state: "loading" })
      const id = ++ssnSearchId.current
      // Dispatched, not sent through useSearchBookingRequestsBySsnMutation: that hook would keep the
      // request (the number included) in this component's state as its originalArgs.
      void runSsnSearch(body, request =>
        dispatch(bookingSearchApi.endpoints.searchBookingRequestsBySsn.initiate(request)),
      ).then(outcome => {
        if (id === ssnSearchId.current) setSsnOutcome(outcome)
      })
      return
    }

    ssnSearchId.current++
    setSsnOutcome(null)
    // Never a number in the query's arguments, even one the user may not search by.
    setSearched({ ...form, ssn: "" })
    setPage(0)
  }

  const reset = () => {
    setForm(defaultSearchForm())
    setSearched(null)
    ssnSearchId.current++
    setSsnOutcome(null)
    setPage(0)
    setSelected(null)
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IconListSearch className="size-6" aria-hidden="true" />
          <h1 className="text-2xl font-bold">Search Appointments</h1>
        </div>
        {request && !ssnOutcome && (
          <Button variant="outline" size="sm" onClick={() => void query.refetch()} disabled={query.isFetching}>
            {query.isFetching ? <IconLoader2 className="size-4 animate-spin" /> : <IconRefresh className="size-4" />}
            Refresh
          </Button>
        )}
      </div>
      <p className="text-muted-foreground max-w-3xl">
        Find appointments at every site, past or upcoming. Choose a date range, and narrow it with any
        other detail — part of the customer&apos;s name or address, their phone number or email, the booking
        reference, their energy account or confirmation number{ssnAllowed ? ", their Social Security number" : ""},
        or a status — or search the range alone. Open a result to see its details.
      </p>

      <SearchCriteriaForm
        form={form}
        validation={validation}
        searching={state === "loading"}
        ssnAllowed={ssnAllowed}
        onChange={setForm}
        onSubmit={search}
        onReset={reset}
      />

      {ssnOutcome ? (
        // One page, no paging, no retry and no refresh: each would need the number again, and it is
        // gone. To search again, it is typed again.
        <SearchResults
          state={ssnOutcome.state}
          data={ssnOutcome.state === "ready" ? ssnOutcome.data : undefined}
          errorMessage={ssnOutcome.state === "error" ? ssnOutcome.message : undefined}
          page={0}
          pageSize={pageSize}
          singlePageOf={SSN_SEARCH_TAKE}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onOpen={setSelected}
        />
      ) : (
        <SearchResults
          state={state}
          data={query.currentData}
          errorMessage={searchErrorMessage(query.error)}
          isFetching={query.isFetching}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={size => { setPageSize(size); setPage(0) }}
          onRetry={() => void query.refetch()}
          onOpen={setSelected}
        />
      )}

      {selected && selectedBooking.isError && (
        <div className="flex items-center justify-between gap-2 rounded-xl border bg-card p-4 text-sm">
          <span className="text-destructive">That appointment could not be loaded from the scheduling system.</span>
          <Button variant="outline" size="sm" onClick={() => setSelected(null)}>Dismiss</Button>
        </div>
      )}

      {selected && (
        <AppointmentDialog
          booking={selectedBooking.data}
          staffName={selected.hostName ?? undefined}
          timeZone={SITE_TIME_ZONE}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}
